// src/components/admin/AdminSettings.js - Component for admin portal settings
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Settings, Save, RefreshCw } from 'lucide-react';
import logger from '../../utils/logger';

const AdminSettings = ({ bcService }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState({
    defaultEnvironment: '',
    enableNotifications: true,
    autoRefreshData: true,
    refreshInterval: 15,
    debugMode: false
  });
  const [savedSettings, setSavedSettings] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        // Try to load settings from localStorage first
        const storedSettings = localStorage.getItem('adminSettings');
        if (storedSettings) {
          const parsedSettings = JSON.parse(storedSettings);
          setSettings(parsedSettings);
          setSavedSettings(parsedSettings);
        }
        
        // In a real implementation, we'd load from the API
        // const response = await bcService.getAdminSettings();
        // setSettings(response);
        // setSavedSettings(response);
      } catch (error) {
        logger.error('Error loading settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, [bcService]);

  // Handle settings change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? parseInt(value, 10) : value
    }));
  };

  // Save settings
  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    
    try {
      // In a real implementation, we'd save to the API
      // await bcService.saveAdminSettings(settings);
      
      // For now, just save to localStorage
      localStorage.setItem('adminSettings', JSON.stringify(settings));
      setSavedSettings(settings);
      
      setMessage({
        type: 'success',
        text: 'Settings saved successfully'
      });
    } catch (error) {
      logger.error('Error saving settings:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save settings: ' + error.message
      });
    } finally {
      setIsSaving(false);
      
      // Clear success message after 3 seconds
      if (message?.type === 'success') {
        setTimeout(() => setMessage(null), 3000);
      }
    }
  };

  // Reset settings to last saved state
  const handleReset = () => {
    setSettings(savedSettings);
    setMessage(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Admin Settings
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure the admin portal settings and preferences
          </p>
        </div>
      </div>
      
      {message && (
        <div className={`mb-6 p-4 rounded-md ${
          message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {message.type === 'success' ? (
                <Save className="h-5 w-5 text-green-400" />
              ) : (
                <Settings className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm ${
                message.type === 'success' ? 'text-green-700' : 'text-red-700'
              }`}>
                {message.text}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">General Settings</h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
            <div>
              <label htmlFor="defaultEnvironment" className="block text-sm font-medium text-gray-700">
                Default Environment
              </label>
              <select
                id="defaultEnvironment"
                name="defaultEnvironment"
                value={settings.defaultEnvironment}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Select a default environment</option>
                <option value="Production">Production</option>
                <option value="Sandbox">Sandbox</option>
                <option value="Development">Development</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                The default environment to select when users log in
              </p>
            </div>
            
            <div className="sm:col-span-2">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <input
                    id="enableNotifications"
                    name="enableNotifications"
                    type="checkbox"
                    checked={settings.enableNotifications}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="enableNotifications" className="font-medium text-gray-700">
                    Enable Admin Notifications
                  </label>
                  <p className="text-gray-500">
                    Receive notifications about user activities and system events
                  </p>
                </div>
              </div>
            </div>
            
            <div className="sm:col-span-2">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <input
                    id="autoRefreshData"
                    name="autoRefreshData"
                    type="checkbox"
                    checked={settings.autoRefreshData}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="autoRefreshData" className="font-medium text-gray-700">
                    Auto-refresh Dashboard Data
                  </label>
                  <p className="text-gray-500">
                    Automatically refresh dashboard data at the specified interval
                  </p>
                </div>
              </div>
            </div>
            
            {settings.autoRefreshData && (
              <div>
                <label htmlFor="refreshInterval" className="block text-sm font-medium text-gray-700">
                  Refresh Interval (minutes)
                </label>
                <input
                  type="number"
                  id="refreshInterval"
                  name="refreshInterval"
                  value={settings.refreshInterval}
                  onChange={handleChange}
                  min="1"
                  max="60"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            )}
            
            {process.env.NODE_ENV === 'development' && (
              <div className="sm:col-span-2">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <input
                      id="debugMode"
                      name="debugMode"
                      type="checkbox"
                      checked={settings.debugMode}
                      onChange={handleChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="debugMode" className="font-medium text-gray-700">
                      Enable Debug Mode
                    </label>
                    <p className="text-gray-500">
                      Show additional debugging information (development only)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3"
          >
            <RefreshCw className="-ml-1 mr-2 h-4 w-4" />
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {isSaving ? (
              <>
                <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Saving...
              </>
            ) : (
              <>
                <Save className="-ml-1 mr-2 h-4 w-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;